// What the abbreviated activity view leaves out.
//
// The activity tab renders a shortened timeline: 5 open tasks, 3 months, and
// 10 items per month. The "הצג את כל הפעילויות" button sits OUTSIDE the blocks
// that apply those caps, so it cannot see whether anything is hidden behind
// them — which is part of why it shipped as a static element with no onClick
// and no state, below a timeline that silently stopped at 10.
//
// This computes the answer once, from the activities the fetch already
// returned. Expanding is a pure render change: no second request, no
// pagination, no backend call.

export const OPEN_TASK_LIMIT = 5;
export const MONTH_LIMIT = 3;
export const ITEMS_PER_MONTH_LIMIT = 10;

// A task is "open" unless its status says it finished. The Hebrew values are
// the ones the CRM actually writes; 'Completed' is the Salesforce original.
export const COMPLETED_TASK_STATUSES = new Set([
  'Completed', 'הושלם',
  'סיום טיפול בשביעות רצון',
  'סיום טיפול לא בשביעות רצון',
]);

export const isOpenTask = (task) => !COMPLETED_TASK_STATUSES.has((task && task.Status) || '');

// The timeline anchors on CreatedDate rather than ActivityDate: ActivityDate is
// date-only, so parsing it yields UTC midnight and every row would render as
// 03:00 in Asia/Jerusalem. Counting has to use the same anchor as rendering,
// or the two disagree about which activities fall in which month.
export const activityDate = (activity, isEvent) => (
  isEvent
    ? (activity.StartDateTime || activity.CreatedDate || activity.ActivityDate)
    : (activity.CreatedDate || activity.ActivityDate)
);

/**
 * @param {{tasks?: Array, events?: Array}} recordActivities — as fetched
 * @returns {{openTasks: Array, hiddenCount: number, hasMore: boolean}}
 *   openTasks   — the open-task list, so the caller need not filter twice
 *   hiddenCount — timeline activities the collapsed view does not render
 *   hasMore     — whether expanding would reveal anything at all
 */
export function summariseActivities(recordActivities) {
  const tasks = (recordActivities && recordActivities.tasks) || [];
  const events = (recordActivities && recordActivities.events) || [];

  const openTasks = tasks.filter(isOpenTask);

  // Sorted newest-first, exactly as the timeline sorts before grouping — the
  // three months counted here must be the three months it renders.
  const dates = [
    ...tasks.map(t => activityDate(t, false)),
    ...events.map(e => activityDate(e, true)),
  ].filter(Boolean).sort((a, b) => new Date(b) - new Date(a));

  // Counts per month in first-appearance order, which for a newest-first list
  // is the order Object.entries() yields in the timeline's grouping.
  const perMonth = [];
  const monthIndex = {};
  dates.forEach(d => {
    const dt = new Date(d);
    const key = `${dt.getFullYear()}-${dt.getMonth()}`;
    if (monthIndex[key] === undefined) {
      monthIndex[key] = perMonth.length;
      perMonth.push(0);
    }
    perMonth[monthIndex[key]] += 1;
  });

  const shown = perMonth
    .slice(0, MONTH_LIMIT)
    .reduce((n, count) => n + Math.min(count, ITEMS_PER_MONTH_LIMIT), 0);

  const hiddenCount = dates.length - shown;
  // The open-task list is capped separately, so it can be truncated even when
  // the timeline shows everything — a record with 8 open tasks this month
  // renders all 8 in the timeline but only 5 above it.
  return {
    openTasks,
    hiddenCount,
    hasMore: hiddenCount > 0 || openTasks.length > OPEN_TASK_LIMIT,
  };
}
